import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../api/client'
import AppleSignInButton from './AppleSignInButton'
import BrandWordmark from './BrandWordmark'
import FacebookSignInButton from './FacebookSignInButton'
import GoogleSignInButton from './GoogleSignInButton'

type AuthMode = 'login' | 'register' | 'forgot' | 'reset'

interface AuthPageProps {
  onLogin: (token: string, user: any) => void
  /** When set (e.g. guest overlay), show “continue browsing” without signing in */
  onCancel?: () => void
  /** Password reset token from email link (?reset_token=) */
  resetToken?: string | null
  /** Called after successful password reset (clear URL param) */
  onResetComplete?: () => void
}

/** UI-only placeholders until OAuth env vars are configured (client demo). */
function GoogleSignInPlaceholder() {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className="auth-social-btn auth-social-btn--google"
      disabled
      title={t('auth.socialUiPreviewTooltip')}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        width={20}
        height={20}
        aria-hidden
      >
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.14l7.73 6c4.51-4.18 7.09-10.36 7.09-17.61z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
      </svg>
      <span>{t('auth.continueWithGoogle')}</span>
    </button>
  )
}

function AppleSignInPlaceholder() {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className="auth-social-btn auth-social-btn--apple auth-social-btn--apple-demo"
      disabled
      title={t('auth.socialUiPreviewTooltip')}
    >
      {t('auth.continueWithApple')}
    </button>
  )
}

export default function AuthPage({ onLogin, onCancel, resetToken, onResetComplete }: AuthPageProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<AuthMode>(resetToken ? 'reset' : 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
  const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined
  const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined
  const [serverOAuth, setServerOAuth] = useState<{
    google?: boolean
    apple?: boolean
    facebook?: boolean
  } | null>(null)

  useEffect(() => {
    authAPI
      .getOAuthStatus()
      .then((res) => setServerOAuth(res.data))
      .catch(() => setServerOAuth(null))
  }, [])

  useEffect(() => {
    if (resetToken) setMode('reset')
  }, [resetToken])

  const googleLive = !!googleClientId?.trim() && serverOAuth?.google !== false
  const appleLive = !!appleClientId?.trim() && serverOAuth?.apple !== false
  const facebookLive = !!facebookAppId?.trim() && serverOAuth?.facebook !== false

  function finishAuthSession(accessToken: string) {
    return authAPI.getCurrentUser(accessToken).then((me) => {
      onLogin(accessToken, me.data)
    })
  }

  function handleLogin() {
    setLoading(true)
    authAPI
      .login(email, password)
      .then((res) => finishAuthSession(res.data.access_token))
      .catch((err) => alert(`${t('auth.loginFailed')}: ${err.response?.data?.detail || err.message}`))
      .finally(() => setLoading(false))
  }

  function handleRegister() {
    setLoading(true)
    authAPI
      .register(email, password, fullName)
      .then(() => authAPI.login(email, password))
      .then((loginRes) => finishAuthSession(loginRes.data.access_token))
      .catch((err) => alert(`${t('auth.registerFailed')}: ${err.response?.data?.detail || err.message}`))
      .finally(() => setLoading(false))
  }

  function handleForgotPassword() {
    setLoading(true)
    authAPI
      .forgotPassword(email)
      .then(() => setForgotSent(true))
      .catch((err) =>
        alert(`${t('auth.forgotPasswordFailed')}: ${err.response?.data?.detail || err.message}`),
      )
      .finally(() => setLoading(false))
  }

  function handleResetPassword() {
    if (!resetToken) return
    if (password !== confirmPassword) {
      alert(t('auth.resetPasswordMismatch'))
      return
    }
    setLoading(true)
    authAPI
      .resetPassword(resetToken, password)
      .then(() => {
        alert(t('auth.resetPasswordSuccess'))
        setPassword('')
        setConfirmPassword('')
        setMode('login')
        onResetComplete?.()
      })
      .catch((err) =>
        alert(`${t('auth.resetPasswordFailed')}: ${err.response?.data?.detail || err.message}`),
      )
      .finally(() => setLoading(false))
  }

  function handleGoogleCredential(credential: string) {
    setLoading(true)
    authAPI
      .oauthGoogle(credential)
      .then((res) => finishAuthSession(res.data.access_token))
      .catch((err) =>
        alert(`${t('auth.oauthGoogleFailed')}: ${err.response?.data?.detail || err.message}`),
      )
      .finally(() => setLoading(false))
  }

  function handleAppleToken(idToken: string, fullNameHint?: string) {
    setLoading(true)
    authAPI
      .oauthApple(idToken, fullNameHint ? { full_name: fullNameHint } : undefined)
      .then((res) => finishAuthSession(res.data.access_token))
      .catch((err) =>
        alert(`${t('auth.oauthAppleFailed')}: ${err.response?.data?.detail || err.message}`),
      )
      .finally(() => setLoading(false))
  }

  function handleFacebookToken(accessToken: string) {
    setLoading(true)
    authAPI
      .oauthFacebook(accessToken)
      .then((res) => finishAuthSession(res.data.access_token))
      .catch((err) =>
        alert(`${t('auth.oauthFacebookFailed')}: ${err.response?.data?.detail || err.message}`),
      )
      .finally(() => setLoading(false))
  }

  function goToLogin() {
    setMode('login')
    setForgotSent(false)
    onResetComplete?.()
  }

  useEffect(() => {
    if (!onCancel) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onCancel])

  const isRecovery = mode === 'forgot' || mode === 'reset'

  const authCard = (
    <div className={`auth-card auth-card--with-social${onCancel ? ' auth-card--modal' : ' helpo-auth-sheet'}`}>
      {onCancel ? (
        <button type="button" className="auth-dismiss auth-dismiss--modal" onClick={onCancel}>
          {t('auth.continueBrowsing')}
        </button>
      ) : null}
      <div className="auth-card-logo" id="auth-modal-title">
        <BrandWordmark className="brand-wordmark--auth" />
      </div>

      {isRecovery ? (
        <>
          <button type="button" className="auth-back-link" onClick={goToLogin}>
            {t('auth.backToLogin')}
          </button>
          <h2 className="auth-recovery-title">
            {mode === 'forgot' ? t('auth.forgotPasswordTitle') : t('auth.resetPasswordTitle')}
          </h2>
          <p className="auth-recovery-lead">
            {mode === 'forgot' ? t('auth.forgotPasswordLead') : t('auth.resetPasswordLead')}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              mode === 'forgot' ? handleForgotPassword() : handleResetPassword()
            }}
          >
            {mode === 'forgot' ? (
              <label>
                {t('auth.email', 'Email')}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={forgotSent}
                />
              </label>
            ) : (
              <>
                <label>
                  {t('auth.resetPasswordNew')}
                  <span className="auth-password-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    >
                      {showPassword ? t('auth.hidePasswordShort') : t('auth.showPasswordShort')}
                    </button>
                  </span>
                </label>
                <label>
                  {t('auth.resetPasswordConfirm')}
                  <span className="auth-password-wrap">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={
                        showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')
                      }
                    >
                      {showConfirmPassword
                        ? t('auth.hidePasswordShort')
                        : t('auth.showPasswordShort')}
                    </button>
                  </span>
                </label>
              </>
            )}
            {!forgotSent ? (
              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading
                  ? '...'
                  : mode === 'forgot'
                    ? t('auth.forgotPasswordSubmit')
                    : t('auth.resetPasswordSubmit')}
              </button>
            ) : (
              <p className="auth-recovery-success" role="status">
                {t('auth.forgotPasswordSent')}
              </p>
            )}
          </form>
        </>
      ) : (
        <>
          <div className="auth-tabs auth-tabs--segmented">
            <button
              type="button"
              className={mode === 'login' ? 'active' : ''}
              onClick={() => setMode('login')}
            >
              {t('auth.login', 'Login')}
            </button>
            <button
              type="button"
              className={mode === 'register' ? 'active' : ''}
              onClick={() => setMode('register')}
            >
              {t('auth.register', 'Register')}
            </button>
          </div>

          <div className="auth-social">
            {!!googleClientId?.trim() && serverOAuth?.google === false && (
              <p className="auth-social-note auth-social-note--warn">{t('auth.googleServerOff')}</p>
            )}
            {!!facebookAppId?.trim() && serverOAuth?.facebook === false && (
              <p className="auth-social-note auth-social-note--warn">{t('auth.facebookServerOff')}</p>
            )}
            {(googleLive || appleLive || facebookLive || import.meta.env.DEV) && (
              <>
                <div className="auth-social-divider">
                  <span>{t('auth.orContinueWith')}</span>
                </div>
                <div className="auth-social-buttons">
                  {googleLive ? (
                    <GoogleSignInButton
                      disabled={loading}
                      onCredential={handleGoogleCredential}
                      onError={() =>
                        alert(
                          `${t('auth.oauthGoogleFailed')}\n\n${t('auth.googleOriginError', {
                            origin: window.location.origin,
                          })}`,
                        )
                      }
                    />
                  ) : import.meta.env.DEV ? (
                    <GoogleSignInPlaceholder />
                  ) : null}
                  {appleLive ? (
                    <AppleSignInButton disabled={loading} onToken={handleAppleToken} />
                  ) : import.meta.env.DEV ? (
                    <AppleSignInPlaceholder />
                  ) : null}
                  {facebookLive ? (
                    <FacebookSignInButton
                      appId={facebookAppId!.trim()}
                      disabled={loading}
                      onAccessToken={handleFacebookToken}
                      onError={(msg) => alert(`${t('auth.oauthFacebookFailed')}: ${msg}`)}
                    />
                  ) : null}
                </div>
              </>
            )}
          </div>

          <div className="auth-form-divider">
            <span>{t('auth.orUseEmail')}</span>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              mode === 'login' ? handleLogin() : handleRegister()
            }}
          >
            {mode === 'register' && (
              <label>
                {t('auth.fullName', 'Full name')}
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </label>
            )}
            <label>
              {t('auth.email', 'Email')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              {t('auth.password', 'Password')}
              <span className="auth-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? t('auth.hidePasswordShort') : t('auth.showPasswordShort')}
                </button>
              </span>
            </label>
            {mode === 'login' ? (
              <button
                type="button"
                className="auth-forgot-link"
                onClick={() => {
                  setForgotSent(false)
                  setMode('forgot')
                }}
              >
                {t('auth.forgotPasswordLink')}
              </button>
            ) : null}
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading
                ? '...'
                : mode === 'login'
                  ? t('auth.login', 'Login')
                  : t('auth.register', 'Register')}
            </button>
          </form>
        </>
      )}
    </div>
  )

  if (onCancel) {
    return (
      <div className="auth-overlay" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <button
          type="button"
          className="auth-overlay__backdrop"
          onClick={onCancel}
          aria-label={t('auth.continueBrowsing')}
        />
        <div className="auth-overlay__panel">
          <div className="auth-overlay__bloom" aria-hidden="true" />
          {authCard}
        </div>
      </div>
    )
  }

  return (
    <section className="helpo-auth-page auth-page">
      <div className="helpo-auth-hero">
        <BrandWordmark className="brand-wordmark--auth" />
        <p>{t('auth.tagline', 'Patikimas naudotų telefonų marketplace.')}</p>
      </div>
      {authCard}
    </section>
  )
}
